import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LogIn, Loader2, Zap, KeyRound, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      toast.success("If this email exists, a password reset link has been sent.");
      setForgotPasswordOpen(false);
      setResetEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      
      switch (user.user_type) {
        case "admin":
          navigate("/admin");
          break;
        case "worker":
          navigate("/worker");
          break;
        case "contractor":
          navigate("/contractor");
          break;
        default:
          navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Hero Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 login-hero items-center justify-center p-12"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1719396687491-d7f1643ad863?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwd29ya2VycyUyMGNvbnN0cnVjdGlvbiUyMHNpdGUlMjBzd2VkZW58ZW58MHx8fHwxNzcwNzIyMzUzfDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="relative z-10 text-white max-w-lg">
          <div className="flex items-center gap-3 mb-6">
            <Zap className="w-12 h-12" />
            <div>
              <h1 className="text-5xl font-bold tracking-tight">A-TRACK</h1>
              <p className="text-lg opacity-80 mt-1">for Aggreko</p>
            </div>
          </div>
          <p className="text-xl opacity-90 font-medium">
            Workforce Management System
          </p>
          <p className="mt-4 text-lg opacity-80">
            Manage your workforce, track projects, and streamline timesheets with our comprehensive management system.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F4F4F5]">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <Zap className="w-10 h-10 text-[#F37021]" />
            <div>
              <h1 className="text-4xl font-bold text-[#333333] tracking-tight">A-TRACK</h1>
              <p className="text-sm text-[#52525B]">for Aggreko</p>
            </div>
          </div>

          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-[#333333] mb-2">SIGN IN</h2>
                <p className="text-[#52525B]">Enter your credentials to access your dashboard</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="email" className="input-label">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-aggreko"
                    placeholder="name@aggreko.se"
                    required
                    data-testid="login-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="input-label">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-aggreko"
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 btn-primary text-base"
                  disabled={isLoading}
                  data-testid="login-submit-button"
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <LogIn className="w-5 h-5 mr-2" />
                      Sign In
                    </>
                  )}
                </Button>

                {/* Privacy Policy Consent */}
                <p className="text-xs text-center text-gray-500 mt-4">
                  By signing in, you agree to our{" "}
                  <Link to="/privacy" className="text-[#F37021] hover:underline" data-testid="privacy-policy-link">
                    Privacy Policy
                  </Link>
                </p>

                <div className="text-center mt-2">
                  <button
                    type="button"
                    onClick={() => setForgotPasswordOpen(true)}
                    className="text-sm text-[#F37021] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                </div>
              </form>

              {/* Forgot Password Dialog */}
              <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-2xl flex items-center gap-2">
                      <KeyRound className="w-6 h-6 text-[#F37021]" />
                      FORGOT PASSWORD
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <p className="text-[#52525B]">
                      Enter your email address and we will send you a link to reset your password.
                    </p>
                    <div>
                      <Label htmlFor="reset-email" className="input-label">
                        Email Address
                      </Label>
                      <Input
                        id="reset-email"
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        className="input-aggreko"
                        placeholder="name@aggreko.se"
                        required
                        data-testid="forgot-password-email-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full btn-primary"
                      disabled={resetLoading}
                      data-testid="forgot-password-submit-button"
                    >
                      {resetLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        "Send Reset Link"
                      )}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

            </CardContent>
          </Card>

          <div className="text-center text-sm text-[#A1A1AA] mt-6 space-y-1">
            <p>Aggreko Sweden AB © {new Date().getFullYear()}</p>
            <p>
              <Link to="/privacy" className="hover:text-[#F37021] flex items-center justify-center gap-1">
                <Shield className="w-3 h-3" />
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
